import { useEffect, useState } from "react";
import {
  AppAvatar,
  AppBorderBox,
  AppBox,
  AppGridBox,
  AppIconButton,
  AppModal,
  AppPaper,
  AppPill,
  AppSkeletonGlobalCardList,
  AppText,
  AppTitle,
} from "../../../../commons/components";
import { useAppDispatch, useAppSelector } from "../../../../hooks/app";
import {
  cleanUpTeamCompetitionInvites,
  fetchManageTeamCompetitionInvites,
} from "../../../../redux/slices/football/admin/team/invitation/manage-team-competition-invite-list-slice";
import { StatusEnum } from "../../../../commons/enums/status-enum";
import { TournamentTeamStatusEnum } from "../../../../commons/enums/tournament-team-status-enum";
import IconInvitation from "../../../../commons/components/icons/invitation";
import IconCheck from "../../../../commons/components/icons/check";
import IconDismiss from "../../../../commons/components/icons/dismiss";
import ability from "../../../../casl/Can";
import { RolePolicyActionEnum } from "../../../../commons/enums/role-policy-action.enum";
import TeamSearch from "../../tournaments/components/TeamSearch";
import { ManageFootballTeam } from "../../../../commons/models/football/admin/manage-football-team";
import ConfirmationModal from "../../../../commons/components/ConfirmationModal";
import { ManageFootballCompetitionInvitation } from "../../../../commons/models/football/admin/manage-football-competition-invitation";
import {
  teamInviteAccept,
  teamInviteReject,
} from "../../../../redux/slices/football/admin/team/invitation/manage-team-competition-accept-reject-slice";
import { ResponseStatusCode } from "../../../../commons/enums/response-status-code.enum";
import { useSnackbar } from "../../../../redux/snackbarProvider";

const TeamCompetitionInvite = () => {
  const dispatch = useAppDispatch();
  const snackBar = useSnackbar();
  const { teamInfo } = useAppSelector((state) => state.footballManageTeam);
  const { teamCompetitionInvitations, teamCompetitionInvitationsStatus } = useAppSelector(
    (state) => state.footballManageTeamCompetitionInviteList
  );
  const [isAcceptInvitation, setIsAcceptInvitation] = useState<boolean>(false);
  const [selectedTeam, setSelectedTeam] = useState<ManageFootballTeam | null>(null);
  const [selectedInvitation, setSelectedInvitation] = useState<ManageFootballCompetitionInvitation | null>(null);
  const [confirmationDialogMode, setConfirmationDialogMode] = useState<"accept" | "reject" | undefined>();

  const getConfirmationMessage = () => {
    if (!selectedInvitation) return "";
    const competitionName = `<strong>${selectedInvitation.competition.title}</strong>`;
    if (confirmationDialogMode === "accept" && selectedTeam) {
      const teamName = `<strong>${selectedTeam.name}</strong>`;
      return `Do you want to send ${teamName} as the participant in the invited tournament "${competitionName}"?`;
    }
    return `Do you want to reject the invitation to join in the invited tournament "${competitionName}"?`;
  };

  useEffect(() => {
    dispatch(cleanUpTeamCompetitionInvites());
    if (teamInfo.id) {
      dispatch(fetchManageTeamCompetitionInvites(teamInfo.id));
    }
  }, [teamInfo]);

  const handleConfirmationAction = () => {
    if (selectedInvitation) {
      const isAccepted = confirmationDialogMode === "accept" && selectedTeam;
      dispatch(
        isAccepted
          ? teamInviteAccept({
              teamId: teamInfo.id,
              inviteId: selectedInvitation.id,
              teamInviteInfo: {
                selectedTeamId: selectedTeam.id,
              },
            })
          : teamInviteReject({
              teamId: teamInfo.id,
              inviteId: selectedInvitation.id,
            })
      )
        .unwrap()
        .then(() => {
          setSelectedTeam(null);
          setSelectedInvitation(null);
          dispatch(fetchManageTeamCompetitionInvites(teamInfo.id));
          const action = confirmationDialogMode === "accept" ? "Accepted" : "Rejected";
          snackBar({
            key: `hosted-tournament-manager-${confirmationDialogMode}`,
            text: `Invitation ${action} Successfully`,
            variant: "success",
          });
        })
        .catch((err) => {
          if (err.status !== ResponseStatusCode.EXCEPTION_FAILED) {
            snackBar({
              key: err.response.data.code,
              text: err.response.data.message,
              variant: "danger",
            });
          }
        });
      setConfirmationDialogMode(undefined);
    }
  };

  const getInvitationColor = (status: string): "success" | "warning" | "danger" => {
    if (status === TournamentTeamStatusEnum.Invited) return "warning";
    if (status === TournamentTeamStatusEnum.Rejected) return "danger";
    return "success";
  };

  const getInvitationMessage = (status: string): string => {
    if (status === TournamentTeamStatusEnum.Invited) return "Invitation: Pending";
    if (status === TournamentTeamStatusEnum.Rejected) return "Invitation: Rejected";
    return "Invitation: Accepted";
  };

  return (
    <>
      {teamCompetitionInvitationsStatus === StatusEnum.Loading ? (
        <AppSkeletonGlobalCardList numberOfItems={2} withText withAvatar withBadge />
      ) : (
        <>
          {teamCompetitionInvitations.length > 0 && (
            <AppBox flexDirection="column" gap="sm">
              <AppTitle as="h5">Invited Events</AppTitle>
              <AppPaper padding="xs" shadow="xs" style={{ maxHeight: "20.5rem" }} className="overflow-y-auto">
                <AppGridBox columns={2} gap="xs">
                  {teamCompetitionInvitations.mapArray((invite) => (
                    <AppBorderBox radius="xs" style={{ alignContent: "center" }}>
                      <AppBox alignItems="center" justifyContent="space-between" px="xs" py="2xs">
                        <AppBox alignItems="center" gap="xs">
                          <AppAvatar
                            size="lg"
                            username={invite.competition.title}
                            src={invite.competition.logo || ""}
                          />
                          <AppBox flexDirection="column">
                            <AppTitle as="h6">{invite.competition.title}</AppTitle>
                            <AppText color="muted" as="label" fontWeight="semibold">
                              Season: {invite.season.title}
                            </AppText>
                            <AppText color="muted" as="label" fontWeight="semibold">
                              Host team: {invite.competition.hostTeam.shortName}
                            </AppText>
                            {invite.selectedTeam && (
                              <AppText color="muted" as="label" fontWeight="semibold">
                                Participant: {invite.selectedTeam.shortName}
                              </AppText>
                            )}
                          </AppBox>
                        </AppBox>
                        <AppBox gap="sm" alignItems="center">
                          <AppPill
                            icon={<IconInvitation width={15} />}
                            label={getInvitationMessage(invite.status)}
                            varient={getInvitationColor(invite.status)}
                            size="md"
                          />
                          {invite.status === TournamentTeamStatusEnum.Invited &&
                            ability.can(RolePolicyActionEnum.UPDATE, teamInfo) && (
                              <AppBox gap="2xs">
                                <AppIconButton
                                  icon={<IconCheck />}
                                  variant="light"
                                  color="success"
                                  onClick={() => {
                                    setSelectedInvitation(invite);
                                    setIsAcceptInvitation(true);
                                  }}
                                />
                                <AppIconButton
                                  icon={<IconDismiss />}
                                  variant="light"
                                  color="danger"
                                  onClick={() => {
                                    setSelectedInvitation(invite);
                                    setConfirmationDialogMode("reject");
                                  }}
                                />
                              </AppBox>
                            )}
                        </AppBox>
                      </AppBox>
                    </AppBorderBox>
                  ))}
                </AppGridBox>
              </AppPaper>
            </AppBox>
          )}
        </>
      )}
      <AppModal
        opened={isAcceptInvitation}
        onClose={() => {
          setIsAcceptInvitation(false);
          setSelectedInvitation(null);
        }}
        titleIcon={<IconCheck />}
        title="Select Team to Send"
        withoutButtons
      >
        <TeamSearch
          parentTeam={teamInfo}
          onSelectedTeam={(team) => {
            setSelectedTeam(team[0]);
            setIsAcceptInvitation(false);
            setConfirmationDialogMode("accept");
          }}
          withoutMultiselect
          isTeamInviteSelection
        />
      </AppModal>
      <ConfirmationModal
        isOpen={confirmationDialogMode !== undefined}
        icon={confirmationDialogMode === "accept" ? <IconCheck /> : <IconDismiss />}
        title={confirmationDialogMode === "accept" ? "Accept Invitation" : "Reject Invitation"}
        message={getConfirmationMessage()}
        confirmButtonLabel="Continue"
        iconColor={confirmationDialogMode === "accept" ? "success" : "danger"}
        onCancel={() => {
          setSelectedInvitation(null);
          setConfirmationDialogMode(undefined);
        }}
        onConfirm={() => {
          handleConfirmationAction();
        }}
      />
    </>
  );
};

export default TeamCompetitionInvite;
